
import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {BoListPanel, IOnOffHandlers} from "bonomo/dist";
import {BetterDataTableHeader} from "@/model/betterVuetifyInterfaces";
import {IStandaloneDeviceTypeOption, IListStandalone} from "@/model/Standalone/standaloneInterfaces";
import Datim from "@/components/shared/Datim.vue";
import StandaloneEditDialog from "@/views/standalone/StandaloneEditDialog.vue";
import PaginationBar from "@/components/shared/PaginationBar.vue";
import SimpleConfirmationDialog from "@/components/shared/SimpleConfirmationDialog.vue";
import StandaloneScreenshotPreview from "@/views/standalone/StandaloneScreenshotPreview.vue";
import RowActions from "@/components/shared/RowActions.vue";
import {IRowAction} from "@/model/IRowAction";
import {IAuthUserData} from "@/store/modules/authModule";
import {EUserPermissions} from "@/model/EUserPermissions";
import {PermissionHelper} from "@/helpers/PermissionHelper";

const standaloneModule = namespace("standaloneModule");
const authModule = namespace("authModule");

@Component({
  components: {
    BoListPanel,
    Datim,
    StandaloneEditDialog,
    PaginationBar,
    SimpleConfirmationDialog,
    StandaloneScreenshotPreview,
    RowActions,
  }
})
export default class StandalonesGlobalList extends Vue {

  @standaloneModule.Getter('allStandaloneList')
  private allStandaloneList!: IListStandalone[];

  @standaloneModule.Getter('allCurrentPage')
  private allCurrentPage!: number;

  @standaloneModule.Getter('allLastPage')
  private allLastPage!: number;

  @standaloneModule.Getter('allTotal')
  private allTotal!: number;

  @standaloneModule.Getter('allPerPage')
  private allPerPage!: number;

  @standaloneModule.Getter('deviceTypeOptions')
  private deviceTypeOptions!: IStandaloneDeviceTypeOption[];

  @standaloneModule.Action('fetchAllStandalones')
  private fetchAllStandalones!: (filters: any) => Promise<any>;

  @standaloneModule.Action('clearAllStandalones')
  private clearAllStandalones!: () => void;

  @standaloneModule.Action('fetchDeviceTypeOptions')
  private fetchDeviceTypeOptions!: () => void;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  private search: string = '';
  private filterStatus: string = '';
  private filterDeviceType: string = '';
  private filterShowDeleted: string = '';
  private filtersExpanded: boolean = false;
  private selectedPerPage: number = 20;
  private searchTimer: any = null;

  private showEditDialog: boolean = false;
  private editingStandalone: IListStandalone | null = null;

  protected submitting: boolean = false;
  protected showRebootConfirmation: boolean = false;
  protected showRefreshConfirmation: boolean = false;
  protected showSshConfirmation: boolean = false;
  protected showMarkDemoConfirmation: boolean = false;
  private activeStandaloneItemId: number = 0;
  private markDemoStandaloneId: number | null = null;
  private activeStandaloneId: number | null = null;
  private activeLockerId: number | null = null;
  private activeDeviceId: string | null = null;

  ons: IOnOffHandlers = {};

  readonly perPageOptions: number[] = [15, 30, 50, 100];

  public created() {
    this.ons = this.$ons({
      'backend.call.OK.rebootLocker': this.onRebootLockerOK,
      'backend.call.KO.rebootLocker': this.onRebootLockerKO,
      'backend.call.OK.sshConnection': this.onSshConnectionOK,
      'backend.call.KO.sshConnection': this.onSshConnectionKO,
      'backend.call.OK.refreshBrowser': this.onRefreshBrowserOK,
      'backend.call.KO.refreshBrowser': this.onRefreshBrowserKO,
      'backend.call.OK.standaloneDemoMarked': this.onMarkDemoOK,
      'backend.call.KO.standaloneDemoMarked': this.onMarkDemoKO,
    });
    this.fetchDeviceTypeOptions();
  }

  get hasActiveFilters(): boolean {
    return !!(this.filterStatus || this.filterDeviceType || this.filterShowDeleted);
  }

  get activeFilterCount(): number {
    return (this.filterStatus ? 1 : 0)
      + (this.filterDeviceType ? 1 : 0)
      + (this.filterShowDeleted ? 1 : 0);
  }

  get statusFilterOptions() {
    return [
      { text: 'Online',  value: 'online',  color: 'green lighten-4', textColor: 'green darken-3' },
      { text: 'Offline', value: 'offline', color: 'red lighten-4',   textColor: 'red darken-3' },
    ];
  }

  get deviceTypeFilterOptions() {
    return this.deviceTypeOptions.map(option => ({
      text: option.text,
      value: option.value,
      color: option.color,
      textColor: option.text_color,
    }));
  }

  get headers(): BetterDataTableHeader[] {
    return [
      { text: this.$t('common.id') as string,                        align: 'start', sortable: false, value: 'standalone_id' },
      { text: this.$t('standalone.detail.name.title') as string,    align: 'start', sortable: false, value: 'name' },
      { text: this.$t('standalone.list.deviceCode') as string,       align: 'start', sortable: false, value: 'device_code' },
      { text: this.$t('common.community') as string,                 align: 'start', sortable: false, value: 'community' },
      { text: this.$t('standalone.list.lastPing') as string,         align: 'start', sortable: false, value: 'last_ping' },
      { text: this.$t('standalone.list.statuses.title') as string,   align: 'start', sortable: false, value: 'health.status' },
      { text: this.$t('standalone.list.deviceType') as string,       align: 'start', sortable: false, value: 'digital_portal_type' },
      { text: this.$t('standalone.list.country') as string,          align: 'start', sortable: false, value: 'country' },
      { text: '',                                                     align: 'end',   sortable: false, value: 'detailButton', width: '1%' },
    ];
  }

  isDeveloper(): boolean {
    return PermissionHelper.hasPermissionAny(this.authUser, EUserPermissions.PERMISSION_DEVELOPER);
  }

  isOperatorForItem(item: IListStandalone): boolean {
    return PermissionHelper.hasPermission(this.authUser, EUserPermissions.PERMISSION_LOCKER_OPERATOR, item.community_id !== null ? item.community_id : 0);
  }

  // Most used first: preview and edit stay visible, the rest go to the row's "more" menu
  rowActions(item: IListStandalone): IRowAction[] {
    const canOperate = this.isOperatorForItem(item) || this.isDeveloper();
    const actions: IRowAction[] = [];
    if (item.device_id) {
      actions.push({ key: 'preview', icon: 'mdi-cellphone', title: this.$t('standalone.list.actions.titles.preview') as string, handler: () => this.getPreviewUrl(item) });
    }
    actions.push({ key: 'edit', icon: 'mdi-pencil', title: this.$t('standalone.detail.title') as string, handler: () => this.onEditClicked(item) });
    if (item.device_id && canOperate) {
      actions.push({ key: 'refresh', icon: 'mdi-web-refresh', title: this.$t('standalone.list.actions.titles.refresh') as string, disabled: this.submitting, handler: () => this.onRefreshClicked(item) });
      actions.push({ key: 'screenshot', icon: 'mdi-camera', title: this.$t('standalone.list.actions.titles.screenshot') as string, disabled: this.submitting, handler: () => this.onScreenshotClicked(item) });
    }
    if (item.install_code && this.isDeveloper()) {
      actions.push({ key: 'markDemo', icon: 'mdi-flask-outline', title: this.$t('standalone.list.actions.titles.markDemo') as string, color: 'purple darken-1', disabled: this.submitting, handler: () => this.onMarkDemoClicked(item) });
    }
    if (item.device_id && this.isDeveloper()) {
      actions.push({ key: 'reboot', icon: 'mdi-restart', title: this.$t('standalone.list.actions.titles.reboot') as string, disabled: this.submitting, handler: () => this.onRebootClicked(item) });
      actions.push({ key: 'ssh', icon: 'mdi-ssh', title: this.$t('standalone.list.actions.titles.ssh') as string, disabled: this.submitting, handler: () => this.onSshConnectionClicked(item) });
    }
    return actions;
  }

  getPreviewUrl(item: IListStandalone): void {
    const route = this.$router.resolve({
      name: 'standalonePreview',
      params: { deviceId: String(item.device_id), lockerId: String(item.locker_id) },
    });
    window.open(route.href, '_blank', 'noopener');
  }

  loadData() {
    this.fetchAllStandalones(this.buildFilters());
  }

  private buildFilters(): any {
    const filters: any = { page: 1, per_page: this.selectedPerPage };
    if (this.search) filters.search = this.search;
    if (this.filterStatus) filters.status = this.filterStatus;
    if (this.filterDeviceType) filters.digital_portal_type = this.filterDeviceType;
    if (this.filterShowDeleted) filters.show_deleted = true;
    return filters;
  }

  onSearchInput() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.reload();
    }, 400);
  }

  onClearSearch() {
    this.reload();
  }

  onFilterChange() {
    this.reload();
  }

  private reload() {
    this.clearAllStandalones();
    this.fetchAllStandalones(this.buildFilters());
  }

  onPageChange(page: number) {
    this.clearAllStandalones();
    const filters: any = this.buildFilters();
    filters.page = page;
    this.fetchAllStandalones(filters);
  }

  onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.reload();
  }

  protected onEditClicked(standalone: IListStandalone) {
    this.editingStandalone = standalone;
    this.showEditDialog = true;
  }

  protected onStandaloneDeleted() {
    this.showEditDialog = false;
    this.editingStandalone = null;
  }

  protected onRebootClicked(item: IListStandalone) {
    this.activeStandaloneItemId = item.standalone_id;
    this.showRebootConfirmation = true;
  }

  protected onRefreshClicked(item: IListStandalone) {
    this.activeStandaloneItemId = item.standalone_id;
    this.showRefreshConfirmation = true;
  }

  protected onSshConnectionClicked(item: IListStandalone) {
    this.activeStandaloneItemId = item.standalone_id;
    this.showSshConfirmation = true;
  }

  protected onScreenshotClicked(item: IListStandalone) {
    this.activeStandaloneId = item.standalone_id;
    this.activeLockerId = item.locker_id;
    this.activeDeviceId = item.device_id ? String(item.device_id) : null;
  }

  protected onMarkDemoClicked(item: IListStandalone) {
    this.markDemoStandaloneId = item.standalone_id;
    this.showMarkDemoConfirmation = true;
  }

  protected onMarkDemo() {
    if (!this.markDemoStandaloneId) {
      return;
    }
    this.submitting = true;
    this.$store.dispatch('standaloneModule/markStandaloneAsDemo', { standaloneId: this.markDemoStandaloneId });
  }

  protected onCloseScreenshot() {
    this.activeStandaloneId = null;
    this.activeLockerId = null;
    this.activeDeviceId = null;
  }

  protected onRebootLocker() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/rebootStandalone', { standaloneId: this.activeStandaloneItemId });
  }

  private onRebootLockerOK() {
    this.submitting = false;
    this.showRebootConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }

  private onRebootLockerKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  protected onRefreshBrowser() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/refreshBrowser', { standaloneId: this.activeStandaloneItemId });
  }

  private onRefreshBrowserOK() {
    this.submitting = false;
    this.showRefreshConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }

  private onRefreshBrowserKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  protected onSshConnection() {
    this.submitting = true;
    this.$store.dispatch('standaloneModule/sshConnection', { standaloneId: this.activeStandaloneItemId });
  }

  private onSshConnectionOK() {
    this.submitting = false;
    this.showSshConfirmation = false;
    this.$uiBus.emit('success', 'OK');
  }

  private onSshConnectionKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }

  private onMarkDemoOK() {
    this.submitting = false;
    this.showMarkDemoConfirmation = false;
    this.markDemoStandaloneId = null;
    this.$uiBus.emit('success', 'OK');
  }

  private onMarkDemoKO() {
    this.submitting = false;
    this.$uiBus.emit('alert', 'KO');
  }
}
